/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {Redirect, Route, Switch, useLocation} from "react-router";
import {getQueryParams} from "helpers/helpers";
import PageNotFound from "pages/Front/PageNotFound/PageNotFound";

import {useTypedSelector} from "helpers/reducers";
import SessionAction from "store/session/SessionAction";
import {useDispatch} from "useDispatch";
import ToastContainer from "components/ToastContainer";
import {useTranslation} from "react-i18next";
import {Flip, toast} from "react-toastify";
import UnloggedLayout from "./UnloggedLayout";
import ArticlesAction from "../store/articles/ArticlesAction";
import {clearCache, clearLocalStorage} from "helpers/store";
import {DEFAULT_BASENAME, HAS_BASENAME, IS_ONLY_BOOKING, languages} from "helpers/config";
import ReservationAction from "store/reservations/ReservationAction";
import {BrowserRouter} from "react-router-dom";
import {Helmet} from "react-helmet";
import useNavigation from "utils/hooks/useNavigation";
import {Driver} from "types/entities/Driver";

import {useCookies} from "utils/hooks/useCookies";
import UnloggedLayoutNew from "layouts/UnloggedLayoutNew";
import LoggedLayout from "layouts/LoggedLayout";
import AdminLayout from "layouts/AdminLayout";
import UnsubscribeNewsletter from "pages/Front/Booking/UnsubscribeNewsletter/UnsubscribeNewsletter";
import UnsubscribeContainer from "pages/Front/Booking/UnsubscribeNewsletter/UnsubscribeContainer";
import BookingLayoutSeller from "pages/Front/Booking/BookingLayoutSeller";
import UnloggedLayoutFullWidth from "layouts/UnloggedLayoutFullWidth";
import AdminDashboard from "pages/Front/AdminDashboard/AdminDashboard";
import AdminStatisticsCalendar from "pages/Front/AdminDashboard/AdminStatisticsCalendar";
import AdminDataTable from "pages/Front/AdminDashboard/AdminDataTable";
import Registration from "pages/Front/Registration/Registration";
import RegistrationLiabilityWaiver from "pages/Front/Registration/RegistrationLiabilityWaiver";
import RegistrationProductSelection from "pages/Front/Registration/RegistrationProductSelection";
import CsvParser from "pages/Front/AdminDashboard/parser/CsvParser";
import Dashboard from "pages/Front/Dashboard/Dashboard";
import Login from "pages/Front/Login/Login";
import Logout from "pages/Front/Logout/Logout";
import Shop from "pages/Front/Shop/Shop";
import UnloggedRegistrationLayout from "layouts/UnloggedRegistrationLayout";
import InfoMessage from "components/InfoMessage";
import BlacklistPostal from "pages/Front/BlacklistPostal/BlacklistPostal";

const Checkin = React.lazy(() => import("pages/Front/Checkin/Checkin"));
const Reservations = React.lazy(() => import("pages/Front/Reservations/Reservations"));
const ReservationHistory = React.lazy(() => import("pages/Front/ReservationHistory/ReservationHistory"));
const Results = React.lazy(() => import("pages/Front/Results/Results"));
const Ranking = React.lazy(() => import("pages/Front/Ranking/Ranking"));
const PurchaseHistory = React.lazy(() => import("pages/Front/PurchaseHistory/PurchaseHistory"));
const Profile = React.lazy(() => import("pages/Front/Profile/Profile"));
const ForgottenPassword = React.lazy(() => import("pages/Front/ForgottenPassword/ForgottenPassword"));
const ConfirmBooking = React.lazy(() => import("pages/Front/Booking/ConfirmBooking/ConfirmBooking"));
const Register = React.lazy(() => import("pages/Front/Register/Register"));
const Referrals = React.lazy(() => import("pages/Front/Referrals/Referrals"));
const Vouchers = React.lazy(() => import("pages/Front/Vouchers/Vouchers"));
const Friends = React.lazy(() => import("pages/Front/Friends/Friends"));

const BookingNew = React.lazy(() => import("pages/Front/Booking/BookingNew"));

const FrontLayout = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const session = useTypedSelector(state => state.sessionState.session);
    const user = (session?.user as Driver) ?? null;
    const profile = useTypedSelector(state => state.sessionState.profile);
    const language = profile && languages.find(language => language.code === profile.lang);
    const location = useLocation();
    const navigation = useNavigation();
    const [shopActive, setShopActive] = useState(false);
    const {cookies, cookiesEnabled} = useCookies();
    // useEffect(() => {
    //     document.title = location.pathname.includes("/booking") ? "Booking" : "Online profile";
    // }, [location.pathname]);

    const getLanguage = (lang_string: string) => {
        return languages.find(language => language.code === lang_string);
    };
    useEffect(() => {
        const queryParams = getQueryParams();
        if (queryParams?.connected) {
            toast.success(t("Connected successfully"));
        }

        if (queryParams?.lang == "en") {
            const lang = getLanguage("en");
            if (lang) {
                dispatch(SessionAction.setLanguage(lang));
            }
        }
        if (session && session.user && session.user.user_type && session.user.user_type !== "guest" && user.username !== "visitor") {
            dispatch(SessionAction.fetchProfile())
                .then(r => {
                    if (language) {
                        dispatch(SessionAction.setLanguage(language));
                    }
                    if (r.status === 401) {
                        dispatch(SessionAction.logout()).then(r => {
                            clearLocalStorage();
                            clearCache();
                            window.location.href = "/";
                        });
                    }
                })
                .catch(err => {
                    console.log("error on session fetchProfile");
                });

            if (!location.pathname.includes("booking")) {
                dispatch(ReservationAction.fetchReservations("current", 1, 10));
                dispatch(ArticlesAction.fetchArticlesWithHall());
                dispatch(SessionAction.fetchNotifications()).then(r => {});
            }
        }
    }, [session]);

    const matomoAnalytics = () => {
        // @ts-ignore
        var _mtm = (window._mtm = window._mtm || []);
        _mtm.push({"mtm.startTime": new Date().getTime(), event: "mtm.Start"});
        var d = document,
            g = d.createElement("script"),
            s = d.getElementsByTagName("script")[0];
        g.async = true;
        g.src = "https://cl.arbopark.ch/stats/matomo/js/container_ceMbFaOY.js";
        // @ts-ignore
        s.parentNode.insertBefore(g, s);
    };
    useEffect(() => {
        if (session && session.user && session.user.user_type && session.user.user_type !== "guest") {
            dispatch(SessionAction.fetchNotifications()).then(r => {});
        }
    }, [location.pathname]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        if (cookies && cookiesEnabled) {
            matomoAnalytics();
        }
    }, [cookies]);

    useEffect(() => {
        //@AI if location.pathname doesnt begin with "/booking", redirect to it
        //this needs to be turned off for a profile
        if (!location.pathname.startsWith("/xyz") && !location.pathname.startsWith("/unsubscribe") && !location.pathname.startsWith("/booking")) {
            window.location.href = "/booking";
        }
    }, []);

    return (
        <BrowserRouter basename={HAS_BASENAME ? "/" + DEFAULT_BASENAME : "/"}>
            <Switch>
                <Route path="/xyz/drivers/blacklist-postal" exact>
                    <Helmet>
                        <title>{navigation.titleText(t("Blacklist postal"))}</title>
                    </Helmet>
                    <UnloggedLayoutNew>
                        <BlacklistPostal />
                    </UnloggedLayoutNew>
                </Route>
                <Route path="/checkin" exact>
                    <Helmet>
                        <title>{navigation.titleText(t("Checkin"))}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Checkin />
                    </LoggedLayout>
                </Route>
                <Route path="/admin/map" exact>
                    <AdminLayout>
                        <AdminDashboard />
                    </AdminLayout>
                </Route>
                <Route path="/admin/stats/:hallId">
                    <AdminLayout>
                        <AdminStatisticsCalendar />
                    </AdminLayout>
                </Route>
                <Route path="/admin/details/:slug">
                    <AdminLayout>
                        <AdminDataTable />
                    </AdminLayout>
                </Route>
                <Route path="/booking/register">
                    <Helmet>
                        <title>{t("Registration")}</title>
                        <meta content={"test"} name={"og:title"} />
                    </Helmet>
                    <UnloggedRegistrationLayout>
                        <Registration />
                    </UnloggedRegistrationLayout>
                </Route>
                <Route path="/booking/account/liability-waiver">
                    <Helmet>
                        <title>{navigation.titleText(t("Liability Waiver"))}</title>
                    </Helmet>
                    <UnloggedRegistrationLayout>
                        <RegistrationLiabilityWaiver />
                    </UnloggedRegistrationLayout>
                </Route>
                <Route path="/booking/account/product-selection">
                    <Helmet>
                        <title>{t("Product selection")}</title>
                    </Helmet>
                    <UnloggedRegistrationLayout>
                        <RegistrationProductSelection />
                    </UnloggedRegistrationLayout>
                </Route>

                <Route path="/admin/data">
                    <AdminLayout>
                        <CsvParser />
                    </AdminLayout>
                </Route>
                <Route path="/dashboard" exact>
                    <Helmet>
                        <title>{t("Dashboard")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Dashboard />
                    </LoggedLayout>
                </Route>
                <Route path="/" exact>
                    <Helmet>
                        <title>{t("Online profile")}</title>
                    </Helmet>
                    {session && session.user && session.user.user_type !== "guest" ? (
                        <LoggedLayout>
                            <Dashboard />
                        </LoggedLayout>
                    ) : (
                        <UnloggedLayout>
                            <Login />
                        </UnloggedLayout>
                    )}
                    {IS_ONLY_BOOKING && <Redirect to={"/booking"} />}
                </Route>
                <Route path="/logout" exact>
                    <Helmet>
                        <title>{t("Logout")}</title>
                    </Helmet>
                    <UnloggedLayout>
                        <Logout />
                    </UnloggedLayout>
                </Route>
                <Route path="/register">
                    <UnloggedLayout>
                        <Register />
                    </UnloggedLayout>
                </Route>
                <Route path="/forgotten-password/:access_token?/:app_id?/:m_clk_id?">
                    <Helmet>
                        <title>{t("Forgotten password")}</title>
                    </Helmet>
                    <UnloggedLayout>
                        <ForgottenPassword />
                    </UnloggedLayout>
                </Route>
                <Route path="/confirm-booking/:package_cart_guid?/:package_cart_token?/:app_id?/:m_clk_id?">
                    <Helmet>
                        <title>{t("Booking confirmation")}</title>
                    </Helmet>
                    <UnloggedLayout>
                        <ConfirmBooking />
                    </UnloggedLayout>
                </Route>

                <Route path="/results" exact>
                    <Helmet>
                        <title>{t("Results")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Results />
                    </LoggedLayout>
                </Route>
                <Route path="/referrals">
                    <Helmet>
                        <title>{t("Referrals")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Referrals />
                    </LoggedLayout>
                </Route>
                <Route path="/friends">
                    <Helmet>
                        <title>{t("Friends")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Friends />
                    </LoggedLayout>
                </Route>
                <Route path="/vouchers">
                    <Helmet>
                        <title>{t("Vouchers")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Vouchers />
                    </LoggedLayout>
                </Route>
                <Route path="/shop/paid">
                    <Helmet>
                        <title>{t("Shop")}</title>
                    </Helmet>
                    <LoggedLayout onShopClick={() => setShopActive(!shopActive)}>
                        <Shop param={1} setShopActive={setShopActive} shopActive={shopActive} />
                    </LoggedLayout>
                </Route>

                <Route path="/shop/:personType?" exact>
                    <Helmet>
                        <title>{t("Shop")}</title>
                    </Helmet>
                    <LoggedLayout onShopClick={() => setShopActive(!shopActive)}>
                        <Shop setShopActive={setShopActive} shopActive={shopActive} />
                    </LoggedLayout>
                </Route>
                <Route path="/ranking" exact>
                    <Helmet>
                        <title>{t("Ranking")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Ranking />
                    </LoggedLayout>
                </Route>
                <Route path="/purchase-history" exact>
                    <Helmet>
                        <title>{t("Purchase history")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <PurchaseHistory />
                    </LoggedLayout>
                </Route>
                <Route path="/profile" exact>
                    <Helmet>
                        <title>{t("Profile")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Profile />
                    </LoggedLayout>
                </Route>
                <Route path="/reservations">
                    <Helmet>
                        <title>{t("Reservations")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <Reservations />
                    </LoggedLayout>
                </Route>
                <Route path="/reservation-history">
                    <Helmet>
                        <title>{t("Reservation history")}</title>
                    </Helmet>
                    <LoggedLayout>
                        <ReservationHistory />
                    </LoggedLayout>
                </Route>

                <Route path="/booking/seller">
                    <Helmet>
                        <title>{navigation.titleText(t("Seller"))}</title>
                    </Helmet>

                    <UnloggedLayoutFullWidth hideLogin={true} showNotification={true}>
                        <BookingLayoutSeller />
                    </UnloggedLayoutFullWidth>
                </Route>
                <Route path="/booking">
                    <Helmet>
                        <title>{navigation.titleText(t("Booking"))}</title>
                    </Helmet>

                    <UnloggedLayoutNew>
                        <BookingNew />
                    </UnloggedLayoutNew>
                </Route>

                <Route path="/unsubscribe">
                    <Helmet>
                        <title>{navigation.titleText(t("Newsletter"))}</title>
                    </Helmet>

                    <UnloggedLayoutNew>
                        <UnsubscribeContainer>
                            <UnsubscribeNewsletter />
                        </UnsubscribeContainer>
                    </UnloggedLayoutNew>
                </Route>

                <Route>
                    <Helmet>
                        <title>{t("Page not found")}</title>
                    </Helmet>
                    <UnloggedLayout>
                        <PageNotFound />
                    </UnloggedLayout>
                </Route>
            </Switch>
            <InfoMessage />
            <ToastContainer autoClose={5000} draggablePercent={50} newestOnTop={true} pauseOnFocusLoss={false} transition={Flip} />
        </BrowserRouter>
    );
};

export default FrontLayout;
